import type {CSSProperties, SVGProps} from "react";
import {token} from "@atlaskit/tokens";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import {clsx} from "~/components/table/tableUtils.tsx";
import {
    canRenderTableEmptyStatePortal,
    getTableEmptyStateOverlayStyle,
    renderTableEmptyStatePortal,
} from "~/components/table/tableEmptyStatePortal";
import {useTablePanelViewportRect} from "~/components/table/useTablePanelViewportRect";
import TableNoSearchResultsIllustration from "~/components/table/tableNoSearchResultsIllustration.svg?react";
import "~/components/table/tableNoSearchResultsEmptyState.css";

export function TableNoSearchResultsEmptyState({
    onClearFilters,
    className,
    illustrationProps,
    viewportBoundsElement,
}: {
    onClearFilters: () => void
    className?: string
    illustrationProps?: SVGProps<SVGSVGElement>
    /** Table panel card; overlay is portaled here with `position: absolute` (parent must be `relative`). */
    viewportBoundsElement: HTMLElement | null
}) {
    const boundsRect = useTablePanelViewportRect(viewportBoundsElement);

    const overlayStyle: CSSProperties | undefined = getTableEmptyStateOverlayStyle({
        viewportBoundsElement: viewportBoundsElement,
        boundsRect: boundsRect,
    });

    const canPortal = canRenderTableEmptyStatePortal({
        viewportBoundsElement: viewportBoundsElement,
        boundsRect: boundsRect,
    });

    const overlay = (
        <div
            className={clsx("table-no-search-results", className)}
            style={overlayStyle}
            role="status"
            aria-live="polite"
        >
            <div className="table-no-search-results__inner">
                <TableNoSearchResultsIllustration
                    aria-hidden
                    {...illustrationProps}
                    className={clsx("table-no-search-results__illustration", illustrationProps?.className)}
                />
                <h2 className="table-no-search-results__title" style={{color: token("color.text")}}>
                    No search results
                </h2>
                <p className="table-no-search-results__subtitle" style={{color: token("color.text")}}>
                    Try a different word, phrase or adjust your Filters
                </p>
                <div className="table-no-search-results__actions">
                    <AnankeButton type="button" appearance="primary" onClick={onClearFilters}>
                        Clear
                    </AnankeButton>
                </div>
            </div>
        </div>
    );

    return (
        <>
            {canPortal
                ? renderTableEmptyStatePortal({
                      overlay: overlay,
                      viewportBoundsElement: viewportBoundsElement,
                      boundsRect: boundsRect,
                  })
                : null}
            {/* Table cell needs height while empty UI is portaled (see .table-container overflow). */}
            <div className="table-no-search-results__table-anchor" aria-hidden />
        </>
    );
}
